
import React, { useState, useEffect, useCallback } from "react";
import AxiosInstance from '../../../AxiosInstance';
import { getImageUrl } from '../../../utils/imageUrl';
import './styles/stepMangement.css'
import FileViewer from "./FileViewer.jsx";
import { FiEye, FiTrash2, FiEdit, FiPlus } from 'react-icons/fi';

const StepsManagement = ({
    cities,
    showModal,
    showConfirmDialog,
    currentServiceId,
    setCurrentServiceId,
    serviceEtapes,
    setServiceEtapes,
    loadServiceEtapes,
    safeSetState,
    isMountedRef,
    setTab,
    currentServiceData,
    setCurrentServiceData

}) => {
    const [arrondissements, setArrondissements] = useState([]);
    const [documentsRequis, setDocumentsRequis] = useState([]);
    const [currentEtapeId, setCurrentEtapeId] = useState(null);
    const [documentsTab, setDocumentsTab] = useState("list");
    const [editingEtape, setEditingEtape] = useState(null);
    const [editingDocument, setEditingDocument] = useState(null);
    const [documentFile, setDocumentFile] = useState(null);
    const [loadingEtapes, setLoadingEtapes] = useState(false);
    // États pour filtrage
    const [selectedArrondissement, setSelectedArrondissement] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [documentPreviewUrl, setDocumentPreviewUrl] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false); // Affichage modale
    const [previewFile, setPreviewFile] = useState(null);
    const [previewModalVisible, setPreviewModalVisible] = useState(false);

    const handleResetFilters = () => {
        setSelectedArrondissement("");
        setSearchQuery("");
    };
    // Dialog pour les étapes
    const [etapeDialog, setEtapeDialog] = useState({
        visible: false,
        etape: {
            titre: "",
            description: "",
            ordre: 1,
            statut: "EN_ATTENTE",
            location_scope: "ALL_CITIES",
            city_id: "",
            arrondissement_ids: [],
            lieu: "",
            horaires: "",
            cout: ""
        }
    });

    // Dialog pour les documents requis
    const [documentDialog, setDocumentDialog] = useState({
        visible: false,
        document: {
            nom: "",
            description: "",
            obligatoire: true,
            modele_disponible: false
        }
    });
    // Charger les arrondissements liés à la ville du service sélectionné
    useEffect(() => {
        if (currentServiceData?.service?.city_id) {
            const fetchArrondissements = async () => {
                try {
                    const res = await AxiosInstance.get(`/api/cities/${currentServiceData.service.city_id}/arrondissements`);
                    if (isMountedRef.current) {
                        safeSetState(
                            setArrondissements,
                            Array.isArray(res.data) ? res.data : res.data.arrondissements || []
                        );

                    }
                } catch (error) {
                    console.error("Erreur lors du chargement des arrondissements:", error);
                    if (isMountedRef.current) {
                        safeSetState(setArrondissements, []);
                    }
                }
            };
            fetchArrondissements();
        }
    }, [currentServiceData, isMountedRef, safeSetState]);

    // Charger arrondissements du service courant
    useEffect(() => {
        if (!currentServiceId) {
            setArrondissements([]);
            return;
        }

        AxiosInstance
            .get(`/api/service-locations/service/${currentServiceId}`)
            .then((res) => {
                const data = res.data;
                const arrs =
                    data?.locationsByCity?.[0]?.locations?.map((loc) => ({
                        _id: loc.arrondissement._id,
                        name: loc.arrondissement.name,
                    })) || [];
                if (isMountedRef.current) {
                    setArrondissements(arrs);
                }
            })
            .catch((err) => {
                console.error("Erreur chargement arrondissements:", err);
                if (isMountedRef.current) {
                    setArrondissements([]);
                }
            });
    }, [currentServiceId, isMountedRef]);
    // Filtrage dynamique des étapes
    const filteredEtapes = serviceEtapes.filter(etape => {
        // 🔹 Filtrer par arrondissement
        if (selectedArrondissement) {
            if (etape.location_scope !== "SPECIFIC_ARRONDISSEMENTS") return false;

            // attention : etape.arrondissement_ids peut contenir des objets {_id} ou juste des string
            const arrondissementIds = etape.arrondissement_ids.map(a => a._id || a);

            if (!arrondissementIds.includes(selectedArrondissement)) return false;
        }

        // 🔹 Filtrer par recherche de titre
        if (searchQuery && !etape.titre.toLowerCase().includes(searchQuery.toLowerCase())) return false;

        return true;
    });
    const loadCurrentServiceData = useCallback(async (serviceId) => {
        try {
            const serviceResponse = await AxiosInstance.get(`/api/services/${serviceId}`);
            const fullService = serviceResponse.data;
            setCurrentServiceData(fullService);
            return fullService;
        } catch (error) {
            console.error("Erreur lors du chargement des données du service:", error);
            setCurrentServiceData(null);
            return null;
        }
    }, [setCurrentServiceData]);

    useEffect(() => {
        console.log("🎯 useEffect déclenché", {
            currentServiceId,
            currentServiceDataId: currentServiceData?.service?.id
        });

        if (currentServiceId && currentServiceData?.service?.id !== currentServiceId) {
            console.log("🔁 Chargement demandé !");
            loadCurrentServiceData(currentServiceId);
        }
    }, [currentServiceId, currentServiceData?.service?.id, loadCurrentServiceData]);

    const handleEtapeChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'arrondissement_ids') {
            const arrondissementId = value;
            const isChecked = checked;

            setEtapeDialog(prev => ({
                ...prev,
                etape: {
                    ...prev.etape,
                    arrondissement_ids: isChecked
                        ? [...prev.etape.arrondissement_ids, arrondissementId]
                        : prev.etape.arrondissement_ids.filter(id => id !== arrondissementId)
                }
            }));
        } else if (name === 'location_scope') {
            // Réinitialiser les champs de localisation quand on change le scope
            setEtapeDialog(prev => ({
                ...prev,
                etape: {
                    ...prev.etape,
                    [name]: value,
                    city_id: value === 'ALL_CITIES' ? "" : prev.etape.city_id,
                    arrondissement_ids: value === 'SPECIFIC_ARRONDISSEMENTS' ? prev.etape.arrondissement_ids : []
                }
            }));
        } else {
            setEtapeDialog(prev => ({
                ...prev,
                etape: {
                    ...prev.etape,
                    [name]: type === "checkbox" ? checked : value
                }
            }));
        }
    };

    // Fonction pour gérer les changements dans le formulaire de document
    const handleDocumentChange = (e) => {
        const { name, value, type, checked } = e.target;
        setDocumentDialog(prev => ({
            ...prev,
            document: {
                ...prev.document,
                [name]: type === "checkbox" ? checked : value
            }
        }));
    };

    // Créer une étape
    const createEtape = async () => {
        if (!currentServiceId) {
            showModal("Veuillez d'abord créer le service");
            return;
        }

        try {
            if (!etapeDialog.etape.titre.trim()) {
                showModal("Le titre de l'étape est requis");
                return;
            }

            const etapeData = {
                service_id: currentServiceId,
                titre: etapeDialog.etape.titre.trim(),
                description: etapeDialog.etape.description?.trim() || "",
                ordre: serviceEtapes.length + 1,
                statut: etapeDialog.etape.statut,
                location_scope: etapeDialog.etape.location_scope
            };

            if (etapeDialog.etape.lieu?.trim()) {
                etapeData.lieu = etapeDialog.etape.lieu.trim();
            }
            if (etapeDialog.etape.horaires?.trim()) {
                etapeData.horaires = etapeDialog.etape.horaires.trim();
            }
            if (etapeDialog.etape.cout && !isNaN(parseFloat(etapeDialog.etape.cout))) {
                etapeData.cout = parseFloat(etapeDialog.etape.cout);
            }

            if (etapeData.location_scope === 'SPECIFIC_CITY') {
                if (!etapeDialog.etape.city_id) {
                    showModal("Veuillez sélectionner une ville pour une étape spécifique à une ville");
                    return;
                }
                etapeData.city_id = etapeDialog.etape.city_id;
            } else if (etapeData.location_scope === 'SPECIFIC_ARRONDISSEMENTS') {
                if (!etapeDialog.etape.city_id) {
                    showModal("Veuillez sélectionner une ville pour une étape spécifique aux arrondissements");
                    return;
                }
                if (!etapeDialog.etape.arrondissement_ids || etapeDialog.etape.arrondissement_ids.length === 0) {
                    showModal("Veuillez sélectionner au moins un arrondissement");
                    return;
                }
                etapeData.city_id = etapeDialog.etape.city_id;
                etapeData.arrondissement_ids = etapeDialog.etape.arrondissement_ids.filter(id => id);

                if (etapeData.arrondissement_ids.length === 0) {
                    showModal("Veuillez sélectionner au moins un arrondissement valide");
                    return;
                }
            }

            const response = await AxiosInstance.post(`/api/etapes`, etapeData);

            if (response.data.success && isMountedRef.current) {
                await loadServiceEtapes(currentServiceId);
                setEtapeDialog({
                    visible: false,
                    etape: {
                        titre: "",
                        description: "",
                        ordre: 1,
                        statut: "EN_ATTENTE",
                        location_scope: "ALL_CITIES",
                        city_id: "",
                        arrondissement_ids: [],
                        lieu: "",
                        horaires: "",
                        cout: ""
                    }
                });
                showModal("Étape créée avec succès !");
            }
        } catch (error) {
            console.error("Erreur lors de la création de l'étape:", error);
            const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
            showModal(`Erreur lors de la création de l'étape: ${errorMessage}`);
        }
    };

    // Modifier une étape
    const updateEtape = async () => {
        if (!editingEtape) return;

        try {
            if (!etapeDialog.etape.titre.trim()) {
                showModal("Le titre de l'étape est requis");
                return;
            }

            const etapeData = {
                titre: etapeDialog.etape.titre.trim(),
                description: etapeDialog.etape.description?.trim() || "",
                statut: etapeDialog.etape.statut,
                location_scope: etapeDialog.etape.location_scope
            };

            if (etapeDialog.etape.lieu?.trim()) {
                etapeData.lieu = etapeDialog.etape.lieu.trim();
            }
            if (etapeDialog.etape.horaires?.trim()) {
                etapeData.horaires = etapeDialog.etape.horaires.trim();
            }
            if (etapeDialog.etape.cout && !isNaN(parseFloat(etapeDialog.etape.cout))) {
                etapeData.cout = parseFloat(etapeDialog.etape.cout);
            }

            if (etapeData.location_scope === 'SPECIFIC_CITY') {
                if (!etapeDialog.etape.city_id) {
                    showModal("Veuillez sélectionner une ville pour une étape spécifique à une ville");
                    return;
                }
                etapeData.city_id = etapeDialog.etape.city_id;
                etapeData.arrondissement_ids = [];
            } else if (etapeData.location_scope === 'SPECIFIC_ARRONDISSEMENTS') {
                if (!etapeDialog.etape.city_id) {
                    showModal("Veuillez sélectionner une ville pour une étape spécifique aux arrondissements");
                    return;
                }
                if (!etapeDialog.etape.arrondissement_ids || etapeDialog.etape.arrondissement_ids.length === 0) {
                    showModal("Veuillez sélectionner au moins un arrondissement");
                    return;
                }
                etapeData.city_id = etapeDialog.etape.city_id;
                etapeData.arrondissement_ids = etapeDialog.etape.arrondissement_ids.filter(id => id);

                if (etapeData.arrondissement_ids.length === 0) {
                    showModal("Veuillez sélectionner au moins un arrondissement valide");
                    return;
                }
            } else {
                etapeData.city_id = null;
                etapeData.arrondissement_ids = [];
            }

            const response = await AxiosInstance.put(`/api/etapes/${editingEtape._id}`, etapeData);

            if (response.data.success && isMountedRef.current) {
                await loadServiceEtapes(currentServiceId);
                setEditingEtape(null);
                setEtapeDialog({
                    visible: false,
                    etape: {
                        titre: "",
                        description: "",
                        ordre: 1,
                        statut: "EN_ATTENTE",
                        location_scope: "ALL_CITIES",
                        city_id: "",
                        arrondissement_ids: [],
                        lieu: "",
                        horaires: "",
                        cout: ""
                    }
                });
                showModal("Étape modifiée avec succès !");
            }
        } catch (error) {
            console.error("Erreur lors de la modification de l'étape:", error);
            const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
            showModal(`Erreur lors de la modification de l'étape: ${errorMessage}`);
        }
    };

    // Supprimer une étape
    const deleteEtape = (etapeId) => {
        showConfirmDialog(
            "Voulez-vous vraiment supprimer cette étape ? Tous les documents requis associés seront également supprimés.",
            async () => {
                try {
                    await AxiosInstance.delete(`/api/etapes/${etapeId}`);
                    if (isMountedRef.current) {
                        await loadServiceEtapes(currentServiceId);
                        showModal("Étape supprimée avec succès !");
                    }
                } catch (error) {
                    console.error("Erreur lors de la suppression:", error);
                    showModal("Erreur lors de la suppression de l'étape");
                }
            }
        );
    };
    // Ouvrir le formulaire d'édition d'une étape
    const startEditEtape = (etape) => {
        setEditingEtape(etape);
        setEtapeDialog({
            visible: true,
            etape: {
                titre: etape.titre || "",
                description: etape.description || "",
                ordre: etape.ordre || 1,
                statut: etape.statut || "EN_ATTENTE",
                location_scope: etape.location_scope || "ALL_CITIES",
                city_id: etape.city_id?._id || etape.city_id || "",
                arrondissement_ids: etape.arrondissement_ids?.map(arr => arr._id || arr) || [],
                lieu: etape.lieu || "",
                horaires: etape.horaires || "",
                cout: etape.cout?.toString() || ""
            }
        });
    };
    // Charger les documents requis d'une étape
    const loadDocumentsRequis = async (etapeId) => {
        try {
            const response = await AxiosInstance.get(`/api/documents/etape/${etapeId}`);
            if (isMountedRef.current) {
                setDocumentsRequis(response.data.data || []);
            }
        } catch (error) {
            console.error("Erreur lors du chargement des documents requis:", error);
            if (isMountedRef.current) {
                setDocumentsRequis([]);
            }
        }
    };
    // Gérer l'ouverture de la section documents
    const openDocumentsSection = (etapeId) => {
        setCurrentEtapeId(etapeId);
        loadDocumentsRequis(etapeId);
        setDocumentsTab("list");
    };
    // Créer un document requis
    const createDocument = async () => {
        if (!currentEtapeId) {
            showModal("Veuillez d'abord sélectionner une étape");
            return;
        }
        try {
            const formData = new FormData();
            formData.append('etape_id', currentEtapeId);
            formData.append('nom', documentDialog.document.nom);
            formData.append('description', documentDialog.document.description);
            formData.append('obligatoire', documentDialog.document.obligatoire);
            formData.append('modele_disponible', documentDialog.document.modele_disponible);

            if (documentFile) {
                formData.append('modele', documentFile);
            }

            const response = await AxiosInstance.post(`/api/documents`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            if (response.data.success && isMountedRef.current) {
                await loadDocumentsRequis(currentEtapeId);
                // Recharger les étapes pour mettre à jour le compteur
                await loadServiceEtapes(currentServiceId);

                setDocumentDialog({
                    visible: false,
                    document: {
                        nom: "",
                        description: "",
                        obligatoire: true,
                        modele_disponible: false
                    }
                });
                setDocumentFile(null);
                setDocumentsTab("list");
                showModal("Document requis créé avec succès !");
            }
        } catch (error) {
            console.error("Erreur lors de la création du document:", error);
            showModal(`Erreur lors de la création du document: ${error.response?.data?.message || error.message}`);
        }
    };

    // Modifier un document requis
    const updateDocument = async () => {
        if (!editingDocument) return;
        try {
            const formData = new FormData();
            formData.append('nom', documentDialog.document.nom);
            formData.append('description', documentDialog.document.description);
            formData.append('obligatoire', documentDialog.document.obligatoire);
            formData.append('modele_disponible', documentDialog.document.modele_disponible);

            if (documentFile) {
                formData.append('modele', documentFile);
            }

            const response = await AxiosInstance.put(`/api/documents/${editingDocument._id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success && isMountedRef.current) {
                await loadDocumentsRequis(currentEtapeId);
                // Recharger les étapes pour mettre à jour le compteur
                await loadServiceEtapes(currentServiceId);

                setEditingDocument(null);
                setDocumentDialog({
                    visible: false,
                    document: {
                        nom: "",
                        description: "",
                        obligatoire: true,
                        modele_disponible: false
                    }
                });
                setDocumentFile(null);
                setDocumentsTab("list");
                showModal("Document requis modifié avec succès !");
            }
        } catch (error) {
            console.error("Erreur lors de la modification du document:", error);
            showModal(`Erreur lors de la modification du document: ${error.response?.data?.message || error.message}`);
        }
    };

    // Supprimer un document requis
    const deleteDocument = (documentId) => {
        showConfirmDialog(
            "Voulez-vous vraiment supprimer ce document requis ?",
            async () => {
                try {
                    await AxiosInstance.delete(`/api/documents/${documentId}`);
                    if (isMountedRef.current) {
                        await loadDocumentsRequis(currentEtapeId);
                        // Recharger les étapes pour mettre à jour le compteur
                        await loadServiceEtapes(currentServiceId);
                        showModal("Document requis supprimé avec succès !");
                    }
                } catch (error) {
                    console.error("Erreur lors de la suppression:", error);
                    showModal("Erreur lors de la suppression du document requis");
                }
            }
        );
    };

    // Ouvrir le formulaire d'édition d'un document
    const startEditDocument = (document) => {
        setEditingDocument(document);
        setDocumentDialog({
            visible: true,
            document: {
                nom: document.nom || "",
                description: document.description || "",
                obligatoire: document.obligatoire !== false,
                modele_disponible: document.modele_disponible === true
            }
        });
        setDocumentsTab("edit");
    };

    return (
        <div className="tab-content active">
            <div className="steps-header">
                <h2><strong className="label-black">🔧 Gestion des Étapes par Localisation</strong></h2>
                <p className="header-subtitle">Définissez les étapes de processus pour votre service selon les différentes localisations</p>
            </div>

            {!currentServiceId ? (
                <div className="empty-state">
                    <div className="empty-state-icon">📋</div>
                    <h3>Aucun service sélectionné</h3>
                    <p>Veuillez d'abord sélectionner un service pour gérer ses étapes de processus.</p>
                    <button className="btn btn-primary btn-lg" onClick={() => setTab("list")}>
                        📄 Sélectionner un service
                    </button>
                </div>
            ) : (
                <>
                    <div className="service-info-card">
                        <div className="service-info-header">
                            <div className="service-icon">🏢</div>
                            {/* <h3>Service: {currentServiceData?.service?.title || `ID: ${currentServiceId}`}</h3>   */}
                            <div className="service-Details">
                                <h3>Service: {currentServiceData?.service?.title || `ID: ${currentServiceId}`}</h3>
                                <p>Gérez les étapes spécifiques à chaque localisation de ce service</p>
                                {currentServiceData && currentServiceData.locations && (
                                    <div className="service-locations-info">
                                        <small>
                                            <strong>Localisations configurées:</strong> {currentServiceData.locations.length}
                                            {currentServiceData.locations.length > 0 && (
                                                <span> - {currentServiceData.locations.map(loc => {
                                                    const city = cities.find(c => c._id === loc.cityId);
                                                    return city?.name || "Ville inconnue";
                                                }).join(', ')}</span>
                                            )}
                                        </small>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="service-stats">
                            <div className="stat-item">
                                <span className="stat-number">{serviceEtapes.length}</span>
                                <span className="stat-label">Étapes totales</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-number">
                                    {serviceEtapes.filter(e => e.location_scope === 'ALL_CITIES').length}
                                </span>
                                <span className="stat-label">Étapes globales</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-number">
                                    {serviceEtapes.filter(e => e.location_scope !== 'ALL_CITIES').length}
                                </span>
                                <span className="stat-label">Étapes localisées</span>
                            </div>
                        </div>
                    </div>

                    {/* Filtres */}
                    <div className="filter-section">
                        <h3><strong className="label-black">Filtres</strong></h3>
                        <div className="filter-grid">

                            <div className="form-group">
                                <label>Filtrer par Arrondissement</label>
                                <select
                                    className="form-control"
                                    value={selectedArrondissement}
                                    onChange={(e) => setSelectedArrondissement(e.target.value)}
                                >
                                    <option value="">Tous les arrondissements</option>
                                    {arrondissements.length > 0 ? (
                                        arrondissements.map((a) => (
                                            <option key={a._id} value={a._id}>{a.name}</option>
                                        ))
                                    ) : (
                                        <option disabled>Aucun arrondissement trouvé</option>
                                    )}
                                </select>


                            </div>


                            <div className="form-group">
                                <label>Recherche par titre</label>
                                <input
                                    className="form-control"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Rechercher une étape..."
                                />
                            </div>

                            <div className="form-group">
                                <button className="btn btn-secondary" onClick={handleResetFilters}>
                                    Réinitialiser
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="locations-etapes-container">
                        <div className="container-header">
                            <h3><strong className="label-black">📍 Gestion des Étapes</strong></h3>

                            <div className="actions-toolbar">
                                <button
                                    className="btn btn-primary btn-with-icon"
                                    onClick={() => {
                                        // Pré-remplir automatiquement selon les localisations du service
                                        let defaultScope = "ALL_CITIES";
                                        let defaultCityId = "";
                                        let defaultArrondissements = [];

                                        if (currentServiceData && currentServiceData.locations && currentServiceData.locations.length > 0) {
                                            const firstLocation = currentServiceData.locations[0];
                                            defaultCityId = firstLocation.cityId;

                                            if (firstLocation.arrondissementIds && firstLocation.arrondissementIds.length > 0) {
                                                defaultScope = "SPECIFIC_ARRONDISSEMENTS";
                                                defaultArrondissements = firstLocation.arrondissementIds;
                                            } else {
                                                defaultScope = "SPECIFIC_CITY";
                                            }
                                        }

                                        setEtapeDialog({
                                            visible: true,
                                            etape: {
                                                titre: "",
                                                description: "",
                                                ordre: serviceEtapes.length + 1,
                                                statut: "EN_ATTENTE",
                                                location_scope: defaultScope,
                                                city_id: defaultCityId,
                                                arrondissement_ids: defaultArrondissements,
                                                lieu: "",
                                                horaires: "",
                                                cout: ""
                                            }
                                        });
                                        setEditingEtape(null);
                                    }}
                                >
                                    <span className="btn-icon">➕</span>
                                    Ajouter une étape
                                </button>

                                <div className="legend">
                                    <div className="legend-item">
                                        <span className="scope-badge global">🌍 Globale</span>
                                        <small>Toutes les villes</small>
                                    </div>
                                    <div className="legend-item">
                                        <span className="scope-badge city">🏙️ Ville</span>
                                        <small>Ville spécifique</small>
                                    </div>
                                    <div className="legend-item">
                                        <span className="scope-badge arrondissement">📍 Arrondissements</span>
                                        <small>Zones spécifiques</small>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {serviceEtapes.length === 0 && !loadingEtapes ? (
                            <div className="empty-steps-state">
                                <div className="empty-icon">📋</div>
                                <h4>Aucune étape définie</h4>
                                <p>Ce service n'a encore aucune étape de processus. Commencez par créer votre première étape.</p>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => {
                                        let defaultScope = "ALL_CITIES";
                                        let defaultCityId = "";
                                        let defaultArrondissements = [];

                                        if (currentServiceData && currentServiceData.locations && currentServiceData.locations.length > 0) {
                                            const firstLocation = currentServiceData.locations[0];
                                            defaultCityId = firstLocation.cityId;

                                            if (firstLocation.arrondissementIds && firstLocation.arrondissementIds.length > 0) {
                                                defaultScope = "SPECIFIC_ARRONDISSEMENTS";
                                                defaultArrondissements = firstLocation.arrondissementIds;
                                            } else {
                                                defaultScope = "SPECIFIC_CITY";
                                            }
                                        }

                                        setEtapeDialog({
                                            visible: true,
                                            etape: {
                                                titre: "",
                                                description: "",
                                                ordre: 1,
                                                statut: "EN_ATTENTE",
                                                location_scope: defaultScope,
                                                city_id: defaultCityId,
                                                arrondissement_ids: defaultArrondissements,
                                                lieu: "",
                                                horaires: "",
                                                cout: ""
                                            }
                                        });
                                        setEditingEtape(null);
                                    }}
                                >
                                    ➕ Créer la première étape
                                </button>
                            </div>
                        ) : loadingEtapes ? (
                            <div className="loading-etapes">
                                <div className="loading-spinner"></div>
                                <p>Chargement des étapes et comptage des documents...</p>
                            </div>
                        ) : (
                            <div className="etapes-grid">
                                {filteredEtapes.length === 0 ? (
                                    <div className="empty-steps-state">
                                        <div className="empty-icon">📋</div>
                                        <h4>Aucune étape trouvée</h4>
                                        <p>Essayez de modifier vos filtres ou votre recherche.</p>
                                    </div>
                                ) : (
                                    filteredEtapes.map((etape, index) => (
                                        <div key={etape._id} className={`etape-card ${etape.location_scope.toLowerCase()}`}>
                                            <div className="etape-card-header">
                                                <div className="etape-number">#{index + 1}</div>
                                                <div className="etape-title-section">
                                                    <h5 className="etape-title">{etape.titre}</h5>
                                                    <span className={`etape-status status-${etape.statut?.toLowerCase()}`}>
                                                        {etape.statut === 'EN_ATTENTE' && '⏳ En attente'}
                                                        {etape.statut === 'EN_COURS' && '🔄 En cours'}
                                                        {etape.statut === 'TERMINEE' && '✅ Terminée'}
                                                        {etape.statut === 'SUSPENDUE' && '⏸️ Suspendue'}
                                                    </span>
                                                </div>
                                                <div className="etape-actions">
                                                    <button
                                                        className="btn-icon-only btn-edit"
                                                        onClick={() => startEditEtape(etape)}
                                                        title="Modifier"
                                                    >
                                                        ✏️
                                                    </button>
                                                    <button
                                                        className="btn-icon-only btn-documents"
                                                        onClick={() => openDocumentsSection(etape._id)}
                                                        title={`Gérer les documents (${etape.documentsCount || 0} document${(etape.documentsCount || 0) > 1 ? 's' : ''})`}
                                                    >
                                                        📄 {(etape.documentsCount || 0) > 0 && (
                                                            <span className="badge-count">{etape.documentsCount}</span>
                                                        )}
                                                    </button>
                                                    <button
                                                        className="btn-icon-only btn-delete"
                                                        onClick={() => deleteEtape(etape._id)}
                                                        title="Supprimer"
                                                    >
                                                        <FiTrash2 size={13} color="#dc3545" />
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="etape-card-body">
                                                {etape.description && (
                                                    <p className="etape-description">
                                                        {etape.description?.substring(0, 150)}
                                                        {etape.description?.length > 150 ? '...' : ''}
                                                    </p>
                                                )}

                                                {(etape.lieu || etape.horaires || etape.cout) && (
                                                    <div className="etape-metadata">
                                                        {etape.lieu && (
                                                            <div className="metadata-item">
                                                                <span className="metadata-icon">📍</span>
                                                                <span className="metadata-text">{etape.lieu}</span>
                                                            </div>
                                                        )}
                                                        {etape.horaires && (
                                                            <div className="metadata-item">
                                                                <span className="metadata-icon">🕐</span>
                                                                <span className="metadata-text">{etape.horaires}</span>
                                                            </div>
                                                        )}
                                                        {etape.cout && (
                                                            <div className="metadata-item">
                                                                <span className="metadata-icon">💰</span>
                                                                <span className="metadata-text">{etape.cout} FCFA</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                <div className="location-info">
                                                    {etape.location_scope === 'ALL_CITIES' && (
                                                        <div className="location-badge global">
                                                            <span className="badge-icon">🌍</span>
                                                            <span className="badge-text">Appliquée à toutes les villes</span>
                                                        </div>
                                                    )}

                                                    {etape.location_scope === 'SPECIFIC_CITY' && etape.city_id && (
                                                        <div className="location-badge city">
                                                            <span className="badge-icon">🏙️</span>
                                                            <span className="badge-text">
                                                                Ville: {typeof etape.city_id === 'object' ? etape.city_id.name : 'Ville spécifique'}
                                                            </span>
                                                        </div>
                                                    )}

                                                    {etape.location_scope === 'SPECIFIC_ARRONDISSEMENTS' && (
                                                        <div className="location-badge arrondissement">
                                                            <span className="badge-icon">📍</span>
                                                            <div className="badge-content">
                                                                <span className="badge-text">
                                                                    Ville: {typeof etape.city_id === 'object' ? etape.city_id.name : 'Ville spécifique'}
                                                                </span>
                                                                <div className="arrondissements-list">
                                                                    <strong>Arrondissements:</strong>
                                                                    <div className="arrondissements-tags">
                                                                        {etape.arrondissement_ids && etape.arrondissement_ids.length > 0 ? (
                                                                            etape.arrondissement_ids.map((arr, idx) => (
                                                                                <span key={idx} className="arrondissement-tag">
                                                                                    {typeof arr === 'object' ? arr.name : arr}
                                                                                </span>
                                                                            ))
                                                                        ) : (
                                                                            <span className="no-arrondissements">Aucun arrondissement spécifié</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        <div className="summary-card">
                            <div className="summary-header">
                                <h4>📊 Résumé</h4>
                            </div>
                            <div className="summary-content">
                                <div className="summary-stats">
                                    <div className="summary-stat">
                                        <span className="stat-label">Total des étapes:</span>
                                        <span className="stat-value">{serviceEtapes.length}</span>
                                    </div>
                                    <div className="summary-stat">
                                        <span className="stat-label">Publication possible:</span>
                                        <span className={`stat-value ${serviceEtapes.length > 0 ? 'success' : 'error'}`}>
                                            {serviceEtapes.length > 0 ? "✅ Oui" : "❌ Non (aucune étape)"}
                                        </span>
                                    </div>
                                </div>

                                {serviceEtapes.length === 0 && (
                                    <div className="publication-warning">
                                        ⚠️ Un service doit avoir au moins une étape pour pouvoir être publié
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                </>
            )}

            {/* Modal pour les étapes - Version améliorée */}
            {etapeDialog.visible && (
                <div className="modal-overlay">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3 className="modal-title">
                                {editingEtape ? "✏️ Modifier l'étape" : "➕ Ajouter une étape"}
                            </h3>
                            <button
                                className="modal-close"
                                onClick={() => {
                                    setEtapeDialog({
                                        visible: false,
                                        etape: {
                                            titre: "",
                                            description: "",
                                            ordre: 1,
                                            statut: "EN_ATTENTE",
                                            location_scope: "ALL_CITIES",
                                            city_id: "",
                                            arrondissement_ids: [],
                                            lieu: "",
                                            horaires: "",
                                            cout: ""
                                        }
                                    });
                                    setEditingEtape(null);
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="form-section">
                                <h4 className="section-title">📝 Informations générales</h4>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label required">Titre de l'étape</label>
                                        <input
                                            type="text"
                                            name="titre"
                                            className="form-input"
                                            value={etapeDialog.etape.titre}
                                            onChange={handleEtapeChange}
                                            placeholder="Ex: Dépôt de dossier"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Statut</label>
                                        <select
                                            name="statut"
                                            className="form-select"
                                            value={etapeDialog.etape.statut}
                                            onChange={handleEtapeChange}
                                        >
                                            <option value="EN_ATTENTE">⏳ En attente</option>
                                            <option value="EN_COURS">🔄 En cours</option>
                                            <option value="TERMINEE">✅ Terminée</option>
                                            <option value="SUSPENDUE">⏸️ Suspendue</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Description</label>
                                    <textarea
                                        name="description"
                                        className="form-textarea"
                                        value={etapeDialog.etape.description}
                                        onChange={handleEtapeChange}
                                        rows="3"
                                        placeholder="Décrivez cette étape en détail..."
                                    />
                                </div>
                            </div>

                            <div className="form-section">
                                <h4 className="section-title">📍 Détails pratiques</h4>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Lieu</label>
                                        <input
                                            type="text"
                                            name="lieu"
                                            className="form-input"
                                            value={etapeDialog.etape.lieu}
                                            onChange={handleEtapeChange}
                                            placeholder="Ex: Mairie, Préfecture..."
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Horaires</label>
                                        <input
                                            type="text"
                                            name="horaires"
                                            className="form-input"
                                            value={etapeDialog.etape.horaires}
                                            onChange={handleEtapeChange}
                                            placeholder="Ex: 8h-16h du lundi au vendredi"
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Coût (en FCFA)</label>
                                    <input
                                        type="number"
                                        name="cout"
                                        className="form-input"
                                        value={etapeDialog.etape.cout}
                                        onChange={handleEtapeChange}
                                        min="0"
                                        step="0.01"
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>

                            <div className="form-section">
                                <h4 className="section-title">🌍 Portée géographique</h4>

                                <div className="form-group">
                                    <label className="form-label required">Portée géographique</label>
                                    <select
                                        name="location_scope"
                                        className="form-select location-scope-select"
                                        value={etapeDialog.etape.location_scope}
                                        onChange={handleEtapeChange}
                                    >
                                        <option value="ALL_CITIES">🌍 Toutes les villes</option>
                                        <option value="SPECIFIC_CITY">🏙️ Ville spécifique</option>
                                        <option value="SPECIFIC_ARRONDISSEMENTS">📍 Arrondissements spécifiques</option>
                                    </select>
                                    <div className="location-scope-help">
                                        {etapeDialog.etape.location_scope === 'ALL_CITIES' && (
                                            <small>Cette étape s'appliquera à toutes les villes du service</small>
                                        )}
                                        {etapeDialog.etape.location_scope === 'SPECIFIC_CITY' && (
                                            <small>Cette étape ne s'appliquera qu'à une ville en particulier</small>
                                        )}
                                        {etapeDialog.etape.location_scope === 'SPECIFIC_ARRONDISSEMENTS' && (
                                            <small>Cette étape ne s'appliquera qu'aux arrondissements sélectionnés</small>
                                        )}
                                    </div>
                                </div>

                                {etapeDialog.etape.location_scope !== 'ALL_CITIES' && (
                                    <div className="form-group location-selector">
                                        <label className="form-label required">Ville</label>
                                        <select
                                            name="city_id"
                                            className="form-select"
                                            value={etapeDialog.etape.city_id}
                                            onChange={handleEtapeChange}
                                            required
                                        >
                                            <option value="">-- Sélectionner une ville --</option>
                                            {Array.isArray(cities) && cities.map(city => (
                                                <option key={city._id} value={city._id}>{city.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {etapeDialog.etape.location_scope === 'SPECIFIC_ARRONDISSEMENTS' && etapeDialog.etape.city_id && (
                                    <div className="form-group">
                                        <label className="form-label required">Arrondissements concernés</label>
                                        <div className="arrondissements-selector">
                                            {Array.isArray(arrondissements) && arrondissements.length > 0 ? (
                                                <div className="checkbox-grid">
                                                    {arrondissements.map(arr => (
                                                        <label key={arr._id} className="checkbox-label">
                                                            <input
                                                                type="checkbox"
                                                                name="arrondissement_ids"
                                                                value={arr._id}
                                                                checked={etapeDialog.etape.arrondissement_ids.includes(arr._id)}
                                                                onChange={handleEtapeChange}
                                                                className="checkbox-input"
                                                            />
                                                            <span className="checkbox-text">{arr.name}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="no-arrondissements">
                                                    <p>Aucun arrondissement disponible pour cette ville.</p>
                                                </div>
                                            )}

                                            {etapeDialog.etape.arrondissement_ids.length > 0 && (
                                                <div className="selected-arrondissements">
                                                    <strong>Sélectionnés ({etapeDialog.etape.arrondissement_ids.length}) :</strong>
                                                    <div className="selected-tags">
                                                        {etapeDialog.etape.arrondissement_ids.map(arrId => {
                                                            const arr = arrondissements.find(a => a._id === arrId);
                                                            return arr ? (
                                                                <span key={arrId} className="selected-tag">
                                                                    {arr.name}
                                                                </span>
                                                            ) : null;
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button
                                className="btn btn-secondary"
                                onClick={() => {
                                    setEtapeDialog({
                                        visible: false,
                                        etape: {
                                            titre: "",
                                            description: "",
                                            ordre: 1,
                                            statut: "EN_ATTENTE",
                                            location_scope: "ALL_CITIES",
                                            city_id: "",
                                            arrondissement_ids: [],
                                            lieu: "",
                                            horaires: "",
                                            cout: ""
                                        }
                                    });
                                    setEditingEtape(null);
                                }}
                            >
                                Annuler
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={editingEtape ? updateEtape : createEtape}
                                disabled={!etapeDialog.etape.titre.trim()}
                            >
                                {editingEtape ? "💾 Modifier l'étape" : "➕ Créer l'étape"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Section Documents Requis - Version améliorée */}
            {currentEtapeId && documentsTab === "list" && (
                <div className="modal-overlay">
                    <div className="modal-container documents-modal">
                        <div className="modal-header">
                            <h3 className="modal-title">
                                📄 Documents requis pour cette étape
                            </h3>
                            <button
                                className="modal-close"
                                onClick={() => {
                                    setCurrentEtapeId(null);
                                    setDocumentsRequis([]);
                                    setDocumentsTab("list");
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="modal-body documents-body">
                            <div className="documents-header-actions">
                                <div className="documents-stats">
                                    <span className="doc-count">{documentsRequis.length}</span>
                                    <span className="doc-label">document{documentsRequis.length > 1 ? 's' : ''} requis</span>
                                </div>
                                <button
                                    className="btn btn-primary btn-with-icon"
                                    onClick={() => {
                                        setEditingDocument(null);
                                        setDocumentDialog({
                                            visible: true,
                                            document: {
                                                nom: "",
                                                description: "",
                                                obligatoire: true,
                                                modele_disponible: false
                                            }
                                        });
                                        setDocumentsTab("add");
                                    }}
                                >
                                    <span className="btn-icon">➕</span>
                                    Ajouter un document
                                </button>
                            </div>
                            {/* Section des documents requis */}
                            {documentsRequis.length === 0 ? (
                                <div className="empty-documents-state">
                                    <div className="empty-icon">📄</div>
                                    <h4>Aucun document requis</h4>
                                    <p>Cette étape n'a pas encore de documents requis. Ajoutez le premier document nécessaire pour cette étape.</p>
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => {
                                            setEditingDocument(null);
                                            setDocumentDialog({
                                                visible: true,
                                                document: {
                                                    nom: "",
                                                    description: "",
                                                    obligatoire: true,
                                                    modele_disponible: false
                                                }
                                            });
                                            setDocumentsTab("add");
                                        }}
                                    >
                                        ➕ Ajouter le premier document
                                    </button>
                                </div>
                            ) : (
                                <div className="documents-grid">
                                    {documentsRequis.map(doc => {
                                        // Construire l'URL complète du fichier
                                        const cleanPath = doc.chemin_modele
                                            ? doc.chemin_modele.replace(/^\/+/g, '').replace(/\\/g, '/')
                                            : null;
                                        const fileUrl = cleanPath ? getImageUrl(cleanPath) : null;

                                        return (
                                            <div key={doc._id} className="document-card">
                                                <div className="document-card-header">
                                                    <div className="document-icon">📄</div>
                                                    <div className="document-title-section">
                                                        <h5 className="document-title">{doc.nom}</h5>
                                                        <div className="document-badges">
                                                            {doc.obligatoire && <span className="doc-badge required">⚠️ Obligatoire</span>}
                                                            {!doc.obligatoire && <span className="doc-badge optional">📝 Optionnel</span>}
                                                            {doc.modele_disponible && <span className="doc-badge template">📋 Modèle disponible</span>}
                                                        </div>
                                                    </div>

                                                    <div className="document-actions">
                                                        {/* Modifier */}
                                                        <button
                                                            className="btn-icon-only btn-edit"
                                                            onClick={() => startEditDocument(doc)}
                                                            title="Modifier le document"
                                                        >
                                                            <FiEdit size={18} color="#28a745" /> {/* vert */}
                                                        </button>

                                                        {/* Supprimer */}
                                                        <button
                                                            className="btn-icon-only btn-delete"
                                                            onClick={() => deleteDocument(doc._id)}
                                                            title="Supprimer le document"
                                                        >
                                                            <FiTrash2 size={18} color="#dc3545" /> {/* rouge */}
                                                        </button>

                                                        {/* Voir */}
                                                        {doc.chemin_modele && (
                                                            <button
                                                                className="btn-icon-only btn-view"
                                                                onClick={() => {
                                                                    if (!fileUrl) return;
                                                                    setPreviewFile(fileUrl);
                                                                    setPreviewModalVisible(true);
                                                                }}
                                                                title="Voir le document"
                                                            >
                                                                <FiEye size={18} color="#007bff" /> {/* bleu */}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="document-meta">
                                                    <small className="creation-date">
                                                        Créé le {new Date(doc.createdAt || Date.now()).toLocaleDateString('fr-FR')}
                                                    </small>
                                                    {doc.updatedAt && doc.updatedAt !== doc.createdAt && (
                                                        <small className="update-date">
                                                            Modifié le {new Date(doc.updatedAt).toLocaleDateString('fr-FR')}
                                                        </small>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* MODALE DE PRÉVISUALISATION */}
                            {previewModalVisible && previewFile && (
                                <div className="modal-overlay">
                                    <div className="modal-container">
                                        <div className="modal-header">
                                            <h3>Prévisualisation</h3>
                                            <button
                                                className="modal-close"
                                                onClick={() => setPreviewModalVisible(false)}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                        <div className="modal-body">
                                            <FileViewer fileUrl={previewFile} />
                                        </div>
                                        <div className="modal-footer" style={{ justifyContent: 'center' }}>
                                            <a
                                                href={previewFile}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-outline-primary"
                                            >
                                                🔗 Ouvrir dans un nouvel onglet
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            )}


                        </div>

                        <div className="modal-footer">
                            <button
                                className="btn btn-secondary"
                                onClick={() => {
                                    setCurrentEtapeId(null);
                                    setDocumentsRequis([]);
                                    setDocumentsTab("list");
                                }}
                            >
                                Fermer
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={() => {
                                    setEditingDocument(null);
                                    setDocumentDialog({
                                        visible: true,
                                        document: {
                                            nom: "",
                                            description: "",
                                            obligatoire: true,
                                            modele_disponible: false
                                        }
                                    });
                                    setDocumentsTab("add");
                                }}
                            >
                                ➕ Ajouter un document
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Modal pour ajouter/modifier un document - Version améliorée */}
            {documentDialog.visible && (
                <div className="modal-overlay">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3 className="modal-title">
                                {editingDocument ? "✏️ Modifier le document" : "➕ Ajouter un document requis"}
                            </h3>
                            <button
                                className="modal-close"
                                onClick={() => {
                                    setDocumentDialog({
                                        visible: false,
                                        document: {
                                            nom: "",
                                            description: "",
                                            obligatoire: true,
                                            modele_disponible: false
                                        }
                                    });
                                    setDocumentFile(null);
                                    setEditingDocument(null);
                                    setDocumentsTab("list");
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="form-section">
                                <h4 className="section-title">📝 Informations du document</h4>

                                <div className="form-group">
                                    <label className="form-label required">Nom du document</label>
                                    <input
                                        type="text"
                                        name="nom"
                                        className="form-input"
                                        value={documentDialog.document.nom}
                                        onChange={handleDocumentChange}
                                        placeholder="Ex: Carte d'identité, Certificat de résidence..."
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Description</label>
                                    <textarea
                                        name="description"
                                        className="form-textarea"
                                        value={documentDialog.document.description}
                                        onChange={handleDocumentChange}
                                        rows="3"
                                        placeholder="Décrivez les spécificités de ce document (format, validité, etc.)"
                                    />
                                </div>
                            </div>

                            <div className="form-section">
                                <h4 className="section-title">⚙️ Configuration</h4>

                                <div className="checkbox-group">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            name="obligatoire"
                                            checked={documentDialog.document.obligatoire}
                                            onChange={handleDocumentChange}
                                            className="checkbox-input"
                                        />
                                        <span className="checkbox-text">
                                            <strong>Document obligatoire</strong>
                                            <small>Ce document est indispensable pour cette étape</small>
                                        </span>
                                    </label>

                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            name="modele_disponible"
                                            checked={documentDialog.document.modele_disponible}
                                            onChange={handleDocumentChange}
                                            className="checkbox-input"
                                        />
                                        <span className="checkbox-text">
                                            <strong>Modèle disponible</strong>
                                            <small>Un modèle/exemple de ce document est fourni</small>
                                        </span>
                                    </label>
                                </div>
                            </div>

                            {documentDialog.document.modele_disponible && (
                                <div className="form-section">
                                    <h4 className="section-title">📎 Fichier modèle</h4>

                                    <div className="form-group">
                                        <label className="form-label">Télécharger un fichier modèle</label>
                                        <input
                                            type="file"
                                            className="form-input file-input"
                                            onChange={(e) => setDocumentFile(e.target.files[0])}
                                            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.png,.gif"
                                        />
                                        <div className="file-input-help">
                                            <small>Types acceptés: PDF, Word, Excel, images</small>
                                            <small>Taille maximale: 10MB</small>
                                        </div>

                                        {documentFile && (
                                            <div className="selected-file">
                                                <span className="file-icon">📎</span>
                                                <span className="file-name">{documentFile.name}</span>
                                                <span className="file-size">({(documentFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button
                                className="btn btn-secondary"
                                onClick={() => {
                                    setDocumentDialog({
                                        visible: false,
                                        document: {
                                            nom: "",
                                            description: "",
                                            obligatoire: true,
                                            modele_disponible: false
                                        }
                                    });
                                    setDocumentFile(null);
                                    setEditingDocument(null);
                                    setDocumentsTab("list");
                                }}
                            >
                                Annuler
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={editingDocument ? updateDocument : createDocument}
                                disabled={!documentDialog.document.nom.trim()}
                            >
                                {editingDocument ? "💾 Modifier le document" : "➕ Créer le document"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StepsManagement;

